<template>
    <Fluid class="field">
        <div class="label">
            <label>Describe your process</label>
            <small :class="{ 'error': tooLong }">
                {{ description.length }}/{{ PROMPT_MAX_LENGTH }}
            </small>
        </div>
        <Message v-if="tooLong" small severity="warn">
            Max description length reached.
        </Message>
        <Textarea
            id="description"
            autofocus
            v-model="description"
            required
            rows="12"
        />
        <small>
            Roughly describe what you want to model. Don't be too precise, you
            will be able to modify the result.
        </small>
    </Fluid>
    <div class="buttons">
        <Button label="Cancel" text severity="secondary" @click="cancel" />
        <Button
            label="Generate"
            severity="primary"
            v-tooltip.top="tooLong && 'Description is too long'"
            :disabled="tooLong"
            @click="generate"
        />
    </div>
</template>

<script setup lang="ts">
import type { DynamicDialogInstance } from "primevue/dynamicdialogoptions";
import Textarea from "primevue/textarea";
import { type Ref, computed, inject, onMounted, ref, watch } from "vue";

import { useHotKeys } from "@/composables/hotkeys";
import {
    PROMPT_MAX_LENGTH,
    useProcessManager,
} from "@/composables/process-manager";

const dialogRef = inject<Ref<DynamicDialogInstance>>("dialogRef");

const { process } = useProcessManager();
const draftKey = computed(
    () => `bpmn_draft_${process?.value?.identity || "unknown"}`
);

const tooLong = computed(() => {
    return description.value.length >= PROMPT_MAX_LENGTH;
});

const description = ref("");

onMounted(() => {
    const saved = localStorage.getItem(draftKey.value);
    if (saved) {
        description.value = saved;
    }
});

watch(description, (newVal) => {
    if (newVal) {
        localStorage.setItem(draftKey.value, newVal);
    } else {
        localStorage.removeItem(draftKey.value);
    }
});

const { addHotKey } = useHotKeys();

const cancel = () => dialogRef?.value.close();

const generate = async () => {
    if (!description.value) {
        return;
    }
    await dialogRef?.value.data.generateCallback(description.value);
    dialogRef?.value.close();
};

/**
 * Save on cmd/ctrl + enter.
 */
addHotKey("ControlOrMeta+Enter", async () => {
    await generate();
});
</script>

<style lang="scss" scoped>
.field {
    padding-bottom: 1em;

    * {
        display: block;
        margin-bottom: 0.5em;
    }
}

.error {
    color: red;
}

.label {
    display: flex;

    small {
        flex: 0;
    }

    label {
        flex: 1;
    }
}

.buttons {
    display: flex;
    justify-content: end;
    margin-top: 1em;
    gap: 1em;
}
</style>
